import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export default function VideoLightbox({ film, onClose }) {
  useEffect(() => {
    if (!film) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [film, onClose]);

  return (
    <AnimatePresence>
      {film && (
        <motion.div
          data-testid="video-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={onClose}
        >
          {/* Top bar */}
          <div className="absolute top-6 left-6 right-6 md:top-10 md:left-10 md:right-10 flex items-start justify-between text-[#FDFBF7] pointer-events-none">
            <div className="pointer-events-auto">
              <div className="font-mono-label text-[10px] text-[#C4A484] mb-2">
                Now Playing · {film.country}
              </div>
              <h3 className="font-heading text-3xl md:text-5xl tracking-tighter">
                {film.couple}
              </h3>
              <p className="font-mono-label text-[10px] text-[#8F8F8F] mt-2">
                {film.location} · {film.year}
              </p>
            </div>
            <button
              data-testid="lightbox-close"
              onClick={onClose}
              aria-label="Close"
              className="pointer-events-auto p-3 border border-white/20 hover:border-[#C4A484] hover:text-[#C4A484] text-[#FDFBF7] transition-all duration-500 rounded-full"
            >
              <X size={18} />
            </button>
          </div>

          {/* Video frame */}
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1] }}
            className="relative w-full max-w-6xl aspect-video bg-black shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              data-testid="lightbox-iframe"
              src={`https://player.vimeo.com/video/${film.vimeo_id}?autoplay=1&title=0&byline=0&portrait=0&color=C4A484`}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={film.couple}
            />
          </motion.div>

          {/* Bottom hint */}
          <div className="absolute bottom-6 left-0 right-0 text-center font-mono-label text-[10px] text-[#8F8F8F]">
            Press ESC or click outside to close
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
